import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import moment from "moment";
import PaginationComponent from "../../Component/Pagination/pagination";
import Loader from "../../Component/Loader/Loader";
import AssignPlanModal from "../Users/modals/AssignPlanModal";
import AssignAffiliateModal from "../Users/modals/AssignAffiliateModal";
import { useNavigate } from "react-router-dom";
import WabaDetailsModal from "./modals/WabaDetailsModal";
import ManageCreditsModal from "./modals/ManageCreditsModal";
// Interface for Stats
interface StatItem {
  value: number;
  growth: number;
}

interface UserStats {
  totalUsers: StatItem;
  activeUsers: StatItem;
  onboardUsers: StatItem;
  offboardUsers: StatItem;
}

const UsersNew = () => {
  const [userList, setUserList] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [isLoader, setIsLoader] = useState<boolean>(true);
  const naviget = useNavigate();

  // Modals
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAssignAffiliateModal, setShowAssignAffiliateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const [showCreditModal, setShowCreditModal] = useState(false);

  const [creditActionType, setCreditActionType] = useState<'add' | 'deduct'>('add');

  const [showWabaModal, setShowWabaModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  // This holds the WABA data you showed in your JSON snippet
  const [selectedUserWabaData, setSelectedUserWabaData] = useState<any>(null);

  // Stats State
  const [stats, setStats] = useState<UserStats>({
    totalUsers: { value: 0, growth: 0 },
    activeUsers: { value: 0, growth: 0 },
    onboardUsers: { value: 0, growth: 0 },
    offboardUsers: { value: 0, growth: 0 }
  });

  // Filters & Bulk Actions
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("thisWeek");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [viewFilter, setViewFilter] = useState("Onboard");
  const [sortOrder, setSortOrder] = useState("newest");

  const [bulkAction, setBulkAction] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Calculate Dates when Dropdown Changes
  useEffect(() => {
    let start = "";
    let end = "";

    switch (dateFilter) {
      case "today":
        start = moment().startOf('day').format('YYYY-MM-DD');
        end = moment().endOf('day').format('YYYY-MM-DD');
        break;
      case "yesterday":
        start = moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD');
        end = moment().subtract(1, 'days').endOf('day').format('YYYY-MM-DD');
        break;
      case "thisWeek":
        start = moment().startOf('week').format('YYYY-MM-DD');
        end = moment().endOf('week').format('YYYY-MM-DD');
        break;
      case "thisMonth":
        start = moment().startOf('month').format('YYYY-MM-DD');
        end = moment().endOf('month').format('YYYY-MM-DD');
        break;
      case "lastMonth":
        start = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
        end = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        break;
      case "allTime":
      default:
        start = "";
        end = "";
    }

    setStartDate(start);
    setEndDate(end);
  }, [dateFilter]);
  const [tabCounts, setTabCounts] = useState({ all: 0, active: 0, onboard: 0, inactive: 0, trash: 0 });
  // 1. Fetch User List (Unaffected by Dates)
  const getUserList = useCallback(async () => {
    try {
      setIsLoader(true);
      const response = await getRequest(
        `/user/userList?page=${page}&limit=50&activeTab=${viewFilter}&search=${searchQuery}&sort=${sortOrder}`
      );

      if (response.status === 'success') {
        setUserList(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotalUsers(response.pagination.total);
        if (response.tabCounts) {
          setTabCounts(response.tabCounts);
        }
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong loading users!");
    } finally {
      setIsLoader(false);
      setSelectedUsers([]); // Reset selection on new fetch
    }
  }, [page, viewFilter, searchQuery, sortOrder]);

  // 2. Fetch Stats Separately (Triggered ONLY by Date changes)
  const fetchStats = useCallback(async () => {
    // Wait for dates to process unless allTime
    if (dateFilter !== "allTime" && (!startDate || !endDate)) return;

    try {
      const response = await getRequest(
        `/user/userStats?startDate=${startDate}&endDate=${endDate}`
      );
      if (response.status === 'success' && response.stats) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error("Failed to load user stats.");
    }
  }, [startDate, endDate, dateFilter]);

  // Trigger List Fetch
  useEffect(() => {
    getUserList();
  }, [getUserList]);

  // Trigger Stats Fetch
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounced Search
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchQuery(searchText);
      setPage(1);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchText]);

  const handleDirectLogin = async (userId: string, isCreditLine: boolean) => {
    try {
      const response = await postRequest(`/user/admin-login-as-user`, { user_id: userId });
      if (response.status === 'success') {
        toast.success("Opening user dashboard...");
        localStorage.setItem("temp_impersonate_data", JSON.stringify(response.data));
        if (isCreditLine) {
          window.open(`https://wa.uomox.com/auto-login?token=${response.token}`, '_blank');
        } else {
          window.open(`https://app.uomox.com/auto-login?token=${response.token}`, '_blank');
        }
      } else {
        toast.error(response.message || "Failed to login as this user");
      }
    } catch (error) {
      toast.error("Something went wrong during direct login.");
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedUsers(userList.map(u => u._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (id: string) => {
    setSelectedUsers(prev =>
      prev.includes(id) ? prev.filter(userId => userId !== id) : [...prev, id]
    );
  };

  const handleApplyBulkAction = async () => {
    if (!bulkAction) return toast.warning("Please select a bulk action");
    if (selectedUsers.length === 0) return toast.warning("Please select at least one user");

    try {
      setIsLoader(true);
      const response = await postRequest(`/user/handleBulkAction`, {
        bulkAction: bulkAction,
        selectedUsers: selectedUsers
      });

      if (response.status === 'success') {
        toast.success("Bulk action applied successfully");
        setBulkAction("");
        getUserList(); // Refresh data
        fetchStats(); // Refresh stats
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Error applying bulk action");
    } finally {
      setIsLoader(false);
    }
  };

  // Check if subscription expired
  const isSubscriptionExpired = (dateString: string) => {
    if (!dateString) return false;
    return moment(dateString).isBefore(moment(), 'day');
  };

  // Reusable Growth Badge
  const GrowthBadge = ({ growth }: { growth: number }) => {
    const isPositive = growth >= 0;
    return (
      <div className="d-flex align-items-center gap-2 mt-1">
        <span className={`badge badge-sr ${isPositive ? 'bg-success-light-5 text-success' : 'bg-danger-light-5 text-danger'} mb-0`}>
          <i className={`ri-arrow-${isPositive ? 'up' : 'down'}-line align-middle`}></i> {Math.abs(growth).toFixed(2)} %
        </span>
        <span className="fs-13 text-muted">vs. previous period</span>
      </div>
    );
  };

  const displayDateRange = (startDate && endDate)
    ? `${moment(startDate).format("MMM DD, YYYY")} - ${moment(endDate).format("MMM DD, YYYY")}`
    : "All Time";

  const getStatusClass = (status: any) => {
    switch (status?.toUpperCase()) {
      case "ACTIVE":
      case "APPROVED":
        return "bg-neon-light-4";
      case "REJECTED":
      case "FAILED":
        return "bg-red-light-4";
      default:
        return "bg-yellow-light-4";
    }
  };


  const handleOpenModal = (user: any) => {
    setSelectedUserId(user);
    setShowWabaModal(true);
  };

  const handleReconcileWallet = async (userId: string) => {
    const isConfirmed = window.confirm("Are you sure you want to reconcile this user's wallet ledger? This will recalculate and fix any drifted balances.");
    if (!isConfirmed) return;

    setIsLoader(true);
    try {
      const response = await postRequest(`/user/reconcile-wallet`, { userId: userId });

      if (response.status === 'success' || response.success) {
        toast.success(response.message || "Wallet reconciled successfully!");
        getUserList(); // Refresh the list to show the updated balance
      } else {
        toast.error(response.message || "Failed to reconcile wallet.");
      }
    } catch (error: any) {
      console.error("Reconcile Error:", error);
      toast.error("Something went wrong while reconciling the wallet.");
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="hk-pg-wrapper">
      {isLoader && <Loader />}
      <div className="hk-pg-body py-0">
        <div className="page-wrap">
          <div className="page-content ps-0">
            <div className="page-detail-wrap">
              <div className="page-body">
                <div data-simplebar className="nicescroll-bar">
                  <div className="container-fluid">

                    <div className="row g-3">
                      <div className="col-lg-12">
                        <div className="card card-border">
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div>
                                <h4 className="mb-1">All Customers & Users</h4>
                                <p className="mb-0">Manage every account on the platform: roles, login-as, status, and trash.</p>
                              </div>
                              <div>
                                <div className="filters d-flex gap-2 align-items-center">
                                  <select
                                    className="form-select form-select-sm w-130p"
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                  >
                                    <option value="thisWeek">This Week</option>
                                    <option value="today">Today</option>
                                    <option value="yesterday">Yesterday</option>
                                    <option value="thisMonth">This Month</option>
                                    <option value="lastMonth">Last Month</option>
                                    {/* <option value="allTime">All Time</option> */}
                                  </select>
                                  <div className="input-group w-220p">
                                    <div className="input-affix-wrapper">
                                      <span className="input-prefix"><i className="ri-calendar-line"></i></span>
                                      <input className="form-control form-control-sm form-wth-icon pe-none" value={displayDateRange} readOnly />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-3">
                              <div className="col">
                                <div className="card bg-blue-light-5 mb-0 h-100">
                                  <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                      <div>
                                        <p className="mb-1 text-muted">Total Users</p>
                                        <h6 className="h2">{stats.totalUsers.value}</h6>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <div className="avatar avatar-sm avatar-icon avatar-soft-white avatar-smmm">
                                          <span className="initial-wrap"><i className="ri-group-line text-primary"></i></span>
                                        </div>
                                      </div>
                                    </div>
                                    <GrowthBadge growth={stats.totalUsers.growth} />
                                  </div>
                                </div>
                              </div>

                              <div className="col">
                                <div className="card bg-green-light-5 mb-0 h-100">
                                  <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                      <div>
                                        <p className="mb-1 text-muted">Active Users</p>
                                        <h6 className="h2">{stats.activeUsers.value}</h6>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <div className="avatar avatar-sm avatar-icon avatar-soft-white avatar-smmm">
                                          <span className="initial-wrap"><i className="ri-store-3-fill text-success"></i></span>
                                        </div>
                                      </div>
                                    </div>
                                    <GrowthBadge growth={stats.activeUsers.growth} />
                                  </div>
                                </div>
                              </div>

                              <div className="col">
                                <div className="card bg-yellow-light-5 mb-0 h-100">
                                  <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                      <div>
                                        <p className="mb-1 text-muted">Onboard Users</p>
                                        <h6 className="h2">{stats.onboardUsers.value}</h6>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <div className="avatar avatar-sm avatar-icon avatar-soft-white avatar-smmm">
                                          <span className="initial-wrap"><i className="ri-store-3-fill text-warning"></i></span>
                                        </div>
                                      </div>
                                    </div>
                                    <GrowthBadge growth={stats.onboardUsers.growth} />
                                  </div>
                                </div>
                              </div>

                              <div className="col">
                                <div className="card bg-purple-light-5 mb-0">
                                  <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                      <div>
                                        <p className="mb-1 text-muted">Blocked Users</p>
                                        <h6 className="h2">-</h6>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <div className="avatar avatar-sm avatar-icon avatar-soft-white avatar-smmm">
                                          <span className="initial-wrap">
                                            <i className="ri-store-3-fill"></i>
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <GrowthBadge growth={0} />
                                  </div>
                                </div>
                              </div>

                              <div className="col">
                                <div className="card bg-red-light-5 mb-0 h-100">
                                  <div className="card-body">
                                    <div className="d-flex justify-content-between">
                                      <div>
                                        <p className="mb-1 text-muted">Inactive Users</p>
                                        <h6 className="h2">{stats.offboardUsers.value}</h6>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <div className="avatar avatar-sm avatar-icon avatar-soft-white avatar-smmm">
                                          <span className="initial-wrap"><i className="ri-store-3-fill text-danger"></i></span>
                                        </div>
                                      </div>
                                    </div>
                                    <GrowthBadge growth={stats.offboardUsers.growth} />
                                  </div>
                                </div>
                              </div>

                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Toolbar */}
                    <div className="row g-3">
                      <div className="col-7 mb-3">
                        <div className="contact-toolbar-left">
                          <div className="d-xxl-flex d-none align-items-center">
                            {viewFilter === "Trash" ?
                              (<select className="form-select form-select-sm w-120p" value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                                <option value="">Bulk actions</option>
                                <option value="5">Restore</option>
                                <option value="6">Permanently delete</option>
                              </select>)
                              : (<select className="form-select form-select-sm w-120p" value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                                <option value="">Bulk actions</option>
                                <option value="1">Active</option>
                                <option value="2">Inactive</option>
                                <option value="3">Move to trash</option>
                              </select>)}
                            <button className="btn btn-sm btn-light btn-border ms-2" onClick={handleApplyBulkAction}>Apply</button>
                          </div>
                          <div className="d-xxl-flex d-none align-items-center form-group mb-0 ms-3">
                            <label className="flex-shrink-0 mb-0 me-2">Sort by:</label>
                            <select
                              className="form-select form-select-sm w-130p"
                              value={viewFilter === "All" ? sortOrder : viewFilter}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === "newest" || val === "oldest") {
                                  setViewFilter("All");
                                  setSortOrder(val);
                                } else {
                                  setViewFilter(val); // Active, Inactive, Blocked, Trash
                                  setSortOrder("newest");
                                }
                                setPage(1);
                              }}
                            >
                              <option value="newest">Newest First</option>
                              <option value="oldest">Oldest First</option>
                              <option value="Active">Active Users</option>
                              <option value="Inactive">Inactive Users</option>
                              <option value="Trash">Trashed Users</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="col-5 mb-3">
                        <div className="contact-toolbar-right">
                          <div className="dataTables_filter">
                            <label>
                              <input
                                className="form-control form-control-sm"
                                placeholder="Search users..."
                                type="search"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                              />
                            </label>
                          </div>
                          <div className="dataTables_paginate-new paging_simple_numbers">
                            <ul className="pagination custom-pagination pagination-simple">
                              <li className={`paginate_button page-item previous ${page === 1 ? 'disabled' : ''}`}>
                                <button type="button" className="page-link" onClick={() => setPage(p => Math.max(1, p - 1))}>
                                  <i className="ri-arrow-left-s-line"></i>
                                </button>
                              </li>
                              <li className={`paginate_button page-item next ${page >= totalPages ? 'disabled' : ''}`}>
                                <button type="button" className="page-link" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                                  <i className="ri-arrow-right-s-line"></i>
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Table */}
                    <div className="row g-3">
                      <div className="col-lg-12">
                        <div className="card card-border">
                          <div className="card-header">
                            <h6 className="mb-0">Users accounts lists</h6>
                          </div>
                          <ul className="nav nav-tabs nav-line nav-custom nav-icon nav-light">
                            <li className="nav-item">
                              <a
                                href="#"
                                className={`nav-link ${viewFilter === "All" ? "active" : ""}`}
                                onClick={(e) => { e.preventDefault(); setViewFilter("All"); setPage(1); }}
                              >
                                All Users <span className="count">{tabCounts.all}</span>
                              </a>
                            </li>
                            <li className="nav-item">
                              <a
                                href="#"
                                className={`nav-link ${viewFilter === "Active" ? "active" : ""}`}
                                onClick={(e) => { e.preventDefault(); setViewFilter("Active"); setPage(1); }}
                              >
                                Active <span className="count">{tabCounts.active}</span>
                              </a>
                            </li>
                            <li className="nav-item">
                              <a
                                href="#"
                                className={`nav-link ${viewFilter === "Onboard" ? "active" : ""}`}
                                onClick={(e) => { e.preventDefault(); setViewFilter("Onboard"); setPage(1); }}
                              >
                                Onboard <span className="count">{tabCounts.onboard}</span>
                              </a>
                            </li>
                            <li className="nav-item">
                              <a
                                href="#"
                                className={`nav-link ${viewFilter === "Inactive" ? "active" : ""}`}
                                onClick={(e) => { e.preventDefault(); setViewFilter("Inactive"); setPage(1); }}
                              >
                                Inactive <span className="count">{tabCounts.inactive}</span>
                              </a>
                            </li>
                            <li className="nav-item">
                              <a
                                href="#"
                                className={`nav-link ${viewFilter === "Trash" ? "active" : ""}`}
                                onClick={(e) => { e.preventDefault(); setViewFilter("Trash"); setPage(1); }}
                              >
                                Trash <span className="count">{tabCounts.trash}</span>
                              </a>
                            </li>
                          </ul>
                          <div className="table-responsive">
                            <table className="table table-hover mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th className="text-center" style={{ width: '30px' }}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input check-select-all"
                                      onChange={handleSelectAll}
                                      checked={userList.length > 0 && selectedUsers.length === userList.length}
                                    />
                                  </th>
                                  <th>User ID</th>
                                  <th>User Details</th>
                                  <th>Company Name</th>
                                  <th className="text-center">OS</th>
                                  <th className="text-center">Status</th>
                                  {/* <th className="text-center">WABA Status</th> */}
                                  <th className="text-center">Credit Line</th>
                                  <th className="text-center">Wallet</th>
                                  {/* <th>Create Date</th>*/}
                                  <th className="text-center">Last Login</th>
                                  <th className="text-center">Subscription</th>
                                  <th className="text-center">Affiliate</th>
                                  <th className="text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {userList && userList.length > 0 ? (
                                  userList.map((item, index) => {
                                    const isExpired = isSubscriptionExpired(item.user_plan_end_date);
                                    return (
                                      <tr key={index}>
                                        <td className="text-center">
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedUsers.includes(item._id)}
                                            onChange={() => handleSelectUser(item._id)}
                                          />
                                        </td>
                                        <td><a href="#" className="fw-bold">#{item.user_unique_id}</a></td>
                                        <td>
                                          <div className="media align-items-center">
                                            <div className="media-head me-2">
                                              <div className="avatar avatar-xss rounded">
                                                <img alt="user" className="avatar-img" src="/dist/img/logo-avatar-3.png" />
                                              </div>
                                            </div>
                                            <div className="media-body">
                                              <h6 className="fs-13 mb-0 eclps-name-175">{item.user_first_name} {item.user_last_name}</h6>
                                              <p className="mb-0 fs-12">{item.user_email}</p>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="mw-150p text-truncate text-high-em sorting_1">
                                          <span>
                                            {item.user_companyName || "-"}
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          {item.user_whatsapp && item.user_whatsapp.isWabaConnected ? (
                                            <i className="ri-check-fill text-success ri-lg"></i>
                                          ) : (
                                            <i className="ri-close-fill text-danger ri-lg"></i>
                                          )}
                                        </td>
                                        <td className="text-center">
                                          {item.user_status === 1 && <span className="badge radius-sm bg-neon-light-4">ACTIVE</span>}
                                          {item.user_status === 0 && <span className="badge radius-sm bg-yellow-light-4">INACTIVE</span>}
                                          {item.user_status === 2 && <span className="badge radius-sm bg-red-light-4">TRASH</span>}
                                        </td>
                                        {/* <td className="text-center">
                                          <span style={{ cursor: "pointer" }} onClick={(e) => {
                                            e.preventDefault();
                                            handleRefreshWabaStatus(item._id);
                                          }} className={`badge radius-sm ${getStatusClass(item?.user_meta_account_info?.account_review_status || "PENDING")}`}>
                                            {item?.user_meta_account_info?.account_review_status || "PENDING"}
                                          </span>
                                        </td> */}
                                        <td className="text-center">
                                          {item.user_is_credit_line_enabled ? (
                                            <i className="ri-check-fill text-success ri-lg"></i>
                                          ) : (
                                            <i className="ri-close-fill text-danger ri-lg"></i>
                                          )}
                                        </td>
                                        {item.user_is_credit_line_enabled ? (
                                        <td className="text-center fw-semibold text-success">
                                          ₹{Number(item.user_wallet_balance || 0).toFixed(2)}
                                        </td>
                                        ):
                                        <td className="text-center">
                                          <i className="ri-close-fill text-danger ri-lg"></i>
                                        </td>
                                        }
                                        <td className="text-center">{moment(item.updatedAt).format("DD MMM, YYYY")}</td>

                                        {/* SUBSCRIPTION EXPIRED HIGHLIGHT */}
                                        <td className={`text-center ${isExpired ? "text-danger fw-bold" : ""}`}>
                                          {item.user_plan_end_date ? moment(item.user_plan_end_date).format("DD MMM, YYYY") : "-"}
                                        </td>

                                        {/* REFERED BY (ADMIN OR AFFILIATE) */}
                                        <td className="text-center">
                                          {item.user_affiliate_id ? (
                                            <span className="badge bg-primary-light-5 text-primary" onClick={() => naviget("/affiliate-details")}>
                                              {item.user_affiliate_id.affiliate_first_name} {item.user_affiliate_id.affiliate_last_name}
                                            </span>
                                          ) : (
                                            <span className="badge bg-secondary-light-5 text-secondary">Admin</span>
                                          )}
                                        </td>

                                        <td className="text-center">
                                          <div className="d-flex align-items-center justify-content-center gap-1">
                                            <div className="d-flex gap-1">
                                              <a href={`/customers-and-users/view-user/${item._id}`} className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover" data-bs-toggle="tooltip" data-placement="top" title="View">
                                                <span className="icon"><i className="ri-eye-line"></i></span>
                                              </a>
                                              <div className="dropdown dropdown-static">
                                                <button className="btn btn-icon btn-sm btn-flush-dark btn-border flush-soft-hover dropdown-toggle no-caret" aria-expanded="false" data-bs-toggle="dropdown">
                                                  <span className="icon"><i className="ri-more-2-fill"></i></span>
                                                </button>
                                                <div className="dropdown-menu dropdown-menu-end" data-popper-placement="bottom-end">
                                                  <a className="dropdown-item" href="#" onClick={(e) => {
                                                    e.preventDefault();
                                                    handleDirectLogin(item._id, item.user_is_credit_line_enabled || false);
                                                  }}>
                                                    <span className="dropdown-icon"><i className="ri-login-circle-line text-primary"></i></span>
                                                    <span>Login as User</span>
                                                  </a>
                                                  <a className="dropdown-item" href="#" onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedUser(item);
                                                    setShowPlanModal(true);
                                                  }}>
                                                    <span className="dropdown-icon"><i className="ri-vip-crown-line"></i></span>
                                                    <span>Assign Plan</span>
                                                  </a>
                                                  <a className="dropdown-item" href="#" onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedUser(item);
                                                    setShowAssignAffiliateModal(true);
                                                  }}>
                                                    <span className="dropdown-icon"><i className="ri-shield-user-line"></i></span>
                                                    <span>Assign Affiliate</span>
                                                  </a>
                                                  {item.user_is_credit_line_enabled ? (
                                                    <a className="dropdown-item" href="#" onClick={(e) => {
                                                      e.preventDefault();
                                                      naviget(`/meta-pricing/${item._id}`);
                                                    }}>
                                                      <span className="dropdown-icon"><i className="ri-price-tag-3-line text-info"></i></span>
                                                      <span>Manage Meta Pricing</span>
                                                    </a>
                                                  ) : ''}
                                                  {item.user_whatsapp && item.user_whatsapp.isWabaConnected && (<a className="dropdown-item" href="#"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      setSelectedUser(item);
                                                      handleOpenModal(item._id)
                                                    }}
                                                  >
                                                    <span className="dropdown-icon"><i className="ri-file-info-line"></i></span>
                                                    <span>WABA Details</span>
                                                  </a>)}
                                                  {item.user_is_credit_line_enabled ? (
                                                    <>
                                                    <a className="dropdown-item" href="#" onClick={(e) => {
                                                      e.preventDefault();
                                                      setSelectedUser(item);
                                                      setShowCreditModal(true);
                                                    }}>
                                                      <span className="dropdown-icon"><i className="ri-wallet-3-line text-warning"></i></span>
                                                      <span>Manage Credits</span>
                                                    </a>
                                                    <a className="dropdown-item" href="#" onClick={(e) => {
                                                    e.preventDefault();
                                                    handleReconcileWallet(item._id);
                                                  }}>
                                                    <span className="dropdown-icon"><i className="ri-refresh-line text-info"></i></span>
                                                    <span>Reconcile Wallet</span>
                                                  </a>
                                                  </>
                                                  ) : ''}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={11}>
                                      <div className="text-center py-4">No Record Found</div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div className="card-footer d-flex justify-content-between align-items-center">
                            <div className="col-sm">
                              <div className="text-muted">
                                Showing <span className="fw-semibold">{userList?.length || 0}</span> of <span className="fw-semibold">{totalUsers || 0}</span> Results
                              </div>
                            </div>
                            {totalPages > 0 && <PaginationComponent totalPages={totalPages} currentPage={page} setCurrentPage={setPage} />}
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AssignPlanModal
        show={showPlanModal}
        onHide={() => setShowPlanModal(false)}
        user={selectedUser}
        onSuccess={() => {
          getUserList();
        }}
      />
      <AssignAffiliateModal
        show={showAssignAffiliateModal}
        onHide={() => { getUserList(); setShowAssignAffiliateModal(false) }}
        userData={selectedUser}
      />
      <WabaDetailsModal
        show={showWabaModal}
        onHide={() => setShowWabaModal(false)}
        userID={selectedUserId}
        initialData={selectedUser?.user_meta_account_info || {}}
        userData={selectedUser || {}}
        onSyncComplete={getUserList}
      />
      <ManageCreditsModal
        show={showCreditModal}
        onHide={() => setShowCreditModal(false)}
        user={selectedUser}
        onSuccess={() => {
          getUserList();
        }}
      />
    </div>
  );
}

export default UsersNew;